import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { ComputeFactory } from '../../models/ComputeFactory'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { API_BASE } from '../../utils/constants'
import { isForVendor } from '../../utils/records'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { GiftView } from './GiftView'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    if (urlParams.id === undefined) {
        return new RecordNotFoundError()
    }

    await RecordFactory.getInstance().fetchGift(urlParams.id)
    const gift = await RecordFactory.getInstance().loadGift(urlParams.id)
    if (gift === undefined) {
        return new RecordNotFoundError()
    }

    if (gift.client === undefined || gift.item === undefined) {
        return new RecordNotFoundError()
    }

    const purchasedTransactions = await ComputeFactory.getInstance().fetchPurchasesWithGift(
        urlParams.id
    )

    return { gift, client: gift.client, giftItem: gift.item, purchasedTransactions }
}

const render = ({
    base: { owner },
    data,
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    usePageTitle('Gift certificate')

    const { client, giftItem, purchasedTransactions } = data

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const [gift, setGift] = useState(data.gift)

    const navigate = useNavigate()
    const { logAndDisplayError } = useErrorHandler()

    const [confirmationModalState, setConfirmationModalState] = useState<{
        title: string
        body: string
        button: { title: string; intent: 'primary' | 'destructive' }
        onClick: () => void
    } | null>(null)
    const [isModalLoading, setIsModalLoading] = useState(false)

    const headerButtons = useMemo(() => {
        const buttons: HeaderButton[] = []

        if (!gift.cancelledDate) {
            buttons.push({
                title: 'Edit',
                onClick: () => navigate(`/gifts/${gift.id}/edit`),
            })
        }

        if (!gift.paidDate && !gift.cancelledDate && gift.chargeAmount) {
            buttons.push({
                title: 'Charge',
                onClick: () => navigate(`/charge/gift/${gift.id}`),
            })
        }
        else if (!gift.paidDate && !gift.cancelledDate) {
            buttons.push({
                title: 'Mark paid',
                onClick: () => navigate(`/charge/gift/${gift.id}/complete`),
            })
        }

        if (!gift.cancelledDate) {
            buttons.push({
                title: 'Resend',
                onClick: () =>
                    setConfirmationModalState({
                        title: 'Resend gift certificate?',
                        body: 'Would you like to resend this gift certificate to this client?',
                        button: { title: 'Resend', intent: 'primary' },
                        onClick: handleResendGift,
                    }),
            })
        }

        if (gift.markPaidDate) {
            buttons.push({
                title: 'Receipt',
                onClick: () =>
                    setConfirmationModalState({
                        title: 'Send receipt',
                        body: 'Would you like to send a receipt to this client?',
                        button: { title: 'Send receipt', intent: 'primary' },
                        onClick: handleSendReceipt,
                    }),
            })
        }

        if (gift.record?.id && gift.capturedDate && !gift.cancelledDate) {
            buttons.push({
                title: 'Form',
                onClick: () => navigate(`/forms/${gift.record?.id}`),
            })
        }

        if (gift.contract && !gift.cancelledDate) {
            buttons.push({
                title: 'Contract',
                onClick: () => window.open(`${API_BASE}/gift/${gift.id}/contract`, '_blank'),
            })
        }

        if (!gift.cancelledDate && !gift.paidDate) {
            buttons.push({
                title: 'Cancel',
                onClick: () =>
                    setConfirmationModalState({
                        title: 'Cancel gift certificate?',
                        body: 'This action cannot be undone.',
                        button: { title: 'Cancel gift certificate', intent: 'primary' },
                        onClick: handleCancel,
                    }),
            })
        }

        if (gift.markPaidDate && isForVendor(gift, owner)) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalState({
                        title: 'Undo mark paid',
                        body: 'Would you like to undo this payment?',
                        button: { title: 'Undo mark paid', intent: 'destructive' },
                        onClick: handleUndoMarkPaid,
                    }),
            })
        }
        else if (gift.cancelledDate) {
            buttons.push({
                title: 'Undo',
                onClick: () =>
                    setConfirmationModalState({
                        title: 'Undo cancellation',
                        body: 'Would you like to undo the gift cancellation?',
                        button: { title: 'Undo cancellation', intent: 'destructive' },
                        onClick: handleUndoCancel,
                    }),
            })
        }

        buttons.push({
            title: 'Copy',
            onClick: () => navigate(`/gifts/${gift.id}/copy`),
        })

        return buttons
    }, [gift, navigate, owner])

    const handleResendGift = async () => {
        setIsModalLoading(true)
        const result = await RecordFactory.getInstance().sendGift(gift, true)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setIsModalLoading(false)
        setConfirmationModalState(null)
    }

    const handleSendReceipt = async () => {
        setIsModalLoading(true)
        const result = await RecordFactory.getInstance().sendReceipt(gift)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setIsModalLoading(false)
        setConfirmationModalState(null)
        navigate(`/messages/${gift.client?.id}`)
    }

    const handleCancel = async () => {
        setIsModalLoading(true)
        const result = await RecordFactory.getInstance().cancelGift(gift)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setIsModalLoading(false)
        setConfirmationModalState(null)
        setGift(result.value)
    }

    const handleUndoMarkPaid = async () => {
        setIsModalLoading(true)
        const result = await RecordFactory.getInstance().rollbackTransaction(gift)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const handleUndoCancel = async () => {
        setIsModalLoading(true)
        const result = await RecordFactory.getInstance().rollbackTransaction(gift)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        window.location.reload()
    }

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^((\/gifts.*\/(new|edit|copy))|(\/charge\/gift\/.*))/,
    })

    return (
        <>
            <BaseRecordView
                title="Gift Certificate"
                backButton={
                    backHistoryEntry !== undefined
                        ? {
                              title: backHistoryEntry.title ?? 'Back',
                              onClick: backHistoryEntry.goBackTo,
                          }
                        : {
                              title: 'Gift certificates',
                              onClick: () => navigate('/dashboards/gifts'),
                          }
                }
                tag={gift.statusTag ?? undefined}
                headerButtons={headerButtons}
            >
                <GiftView
                    gift={gift}
                    isWorkflowReviewStep={false}
                    client={gift.customer ?? client}
                    giftItem={giftItem}
                    purchasedTransactions={purchasedTransactions}
                />
            </BaseRecordView>

            {confirmationModalState !== null && (
                <Modal
                    isOpen
                    onClose={() => setConfirmationModalState(null)}
                    size="small"
                    showDividers={false}
                    title={confirmationModalState.title}
                    footer={
                        <Button
                            intent={confirmationModalState.button.intent}
                            isLoading={isModalLoading}
                            onClick={confirmationModalState.onClick}
                        >
                            {confirmationModalState.button.title}
                        </Button>
                    }
                >
                    {confirmationModalState.body}
                </Modal>
            )}
        </>
    )
}

export const GiftPage = usePSComponent(render, viewWillAppear)
